import { useState } from 'react';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useFraudReviews, useFraudReviewDonor, useResolveFraudReview } from '../hooks/useAdminOps';
import {
  type FraudEscalationAction,
  type FraudReviewDisposition,
  type FraudReviewDonorItem,
} from '../api/adminOps';
import {
  canSubmitResolution,
  donorName,
  findingSentence,
  reasonLabel,
  reasonsWithoutFindings,
  reasonTone,
  RATIONALE_MAX_LENGTH,
  SEVERITY_PRESENTATION,
  SUSPENSION_MAX_DAYS,
  SUSPENSION_MIN_DAYS,
} from './fraudReview.logic';
import { QueueTable, StatusChip, DetailDrawer, type QueueColumn } from '../components/ui';
import FraudReviewCaseView from './FraudReviewCaseView';

const DISPOSITIONS: { value: FraudReviewDisposition; label: string; hint: string }[] = [
  { value: 'BENIGN', label: 'Benign', hint: 'Explained — backfill or a genuine giving pattern.' },
  {
    value: 'MONITOR',
    label: 'Monitor',
    hint: 'Real but not actionable yet. A new flag re-raises it.',
  },
  { value: 'CONFIRMED', label: 'Confirmed', hint: 'Abuse established. Annotates the record only.' },
  {
    value: 'ESCALATE',
    label: 'Escalate',
    hint: 'Hand to enforcement. A second admin must approve.',
  },
];

const ESCALATIONS: { value: FraudEscalationAction; label: string }[] = [
  { value: 'WARN', label: 'Warn the donor' },
  { value: 'SUSPEND', label: 'Suspend the account' },
  { value: 'UNSHARE', label: 'Remove from public surfaces' },
];

function ErrorCard({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="glass-card p-6 border border-red-500/30 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div>
        <p className="text-white font-medium mb-1">{title}</p>
        <p className="text-sm text-gray-text mb-3">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <button onClick={onRetry} className="btn-secondary" type="button">
          Retry
        </button>
      </div>
    </div>
  );
}

export default function FraudReview() {
  const [selected, setSelected] = useState<FraudReviewDonorItem | null>(null);
  const [disposition, setDisposition] = useState<FraudReviewDisposition | ''>('');
  const [rationale, setRationale] = useState('');
  const [escalation, setEscalation] = useState<FraudEscalationAction | ''>('');
  const [durationDays, setDurationDays] = useState('');

  const queue = useFraudReviews();
  const donorCase = useFraudReviewDonor(selected?.user_id ?? null);
  const resolve = useResolveFraudReview();

  const closeDrawer = () => {
    setSelected(null);
    setDisposition('');
    setRationale('');
    setEscalation('');
    setDurationDays('');
  };

  const trimmedRationale = rationale.trim();
  const escalating = disposition === 'ESCALATE';
  const suspending = escalating && escalation === 'SUSPEND';
  // A failed detail request renders as an empty list, so resolution stays
  // blocked until the evidence has actually loaded.
  const evidenceLoaded = donorCase.isSuccess;
  const canSubmit =
    !resolve.isPending &&
    canSubmitResolution({ disposition, rationale, escalation, durationDays, evidenceLoaded });

  const submit = () => {
    if (!selected || disposition === '' || !canSubmit) return;
    resolve.mutate(
      {
        userId: selected.user_id,
        payload: {
          disposition,
          rationale: trimmedRationale,
          ...(escalating && escalation !== '' ? { escalation } : {}),
          ...(suspending ? { duration_days: Number(durationDays) } : {}),
        },
      },
      { onSuccess: closeDrawer }
    );
  };

  const columns: QueueColumn<FraudReviewDonorItem>[] = [
    {
      key: 'donor',
      header: 'Donor',
      render: row => {
        const severity = SEVERITY_PRESENTATION[row.severity];
        return (
          <div className="flex gap-3">
            <span
              aria-hidden="true"
              data-testid="severity-stripe"
              className={`w-1 self-stretch rounded ${severity.stripe}`}
            />
            <div className="min-w-0">
              <div className="font-medium">{donorName(row) ?? 'Unnamed donor'}</div>
              <div className="text-xs text-muted break-all">{row.user_id}</div>
              <div className="mt-1">
                <StatusChip tone={severity.tone} label={`${severity.label} severity`} />
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'reasons',
      header: 'Why it flagged',
      render: row => (
        <div className="space-y-1">
          {row.findings.map(finding => (
            <div key={finding.code} className="text-xs">
              {findingSentence(finding)}
            </div>
          ))}
          <div className="flex flex-wrap gap-1">
            {reasonsWithoutFindings(row.reasons, row.findings).map(reason => (
              <StatusChip key={reason} tone={reasonTone(reason)} label={reasonLabel(reason)} />
            ))}
          </div>
        </div>
      ),
    },
    {
      key: 'count',
      header: 'Flagged',
      render: row => <span className="tabular-nums">{row.flagged_count}</span>,
    },
    {
      key: 'age',
      header: 'Waiting since',
      render: row => (
        <span className="text-xs text-muted">
          oldest flag {formatDistanceToNow(new Date(row.first_flagged_at), { addSuffix: true })}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-2">
        <ShieldAlert className="h-5 w-5" aria-hidden="true" />
        <h1 className="text-lg font-semibold">Fraud review</h1>
      </header>

      <p className="text-sm text-muted">
        Donors with donations the velocity detector flagged. Reviewing a case never changes anything
        for the donor — the only user-visible outcome is an escalation, and that needs a second
        admin&apos;s approval.
      </p>

      {queue.isError ? (
        <ErrorCard
          title="Failed to load the fraud review queue"
          error={queue.error}
          onRetry={() => queue.refetch()}
        />
      ) : (
        <QueueTable
          columns={columns}
          rows={queue.rows}
          rowKey={row => row.user_id}
          isLoading={queue.isPending}
          onRowClick={setSelected}
          rowActionLabel={row => `Open fraud case for ${donorName(row) ?? row.user_id}`}
          emptyTitle="Nothing awaiting fraud review"
          emptyHint="Flagged donors appear here. An empty queue is the expected steady state."
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onLoadMore={queue.fetchNextPage}
        />
      )}

      <DetailDrawer
        open={selected !== null}
        title={selected ? `Fraud review — ${donorName(selected) ?? selected.user_id}` : ''}
        onClose={closeDrawer}
        footer={
          <div className="space-y-3">
            <div>
              <label className="label" htmlFor="fraud-disposition">
                Disposition
              </label>
              <select
                id="fraud-disposition"
                className="input"
                value={disposition}
                onChange={event => {
                  setDisposition(event.target.value as FraudReviewDisposition | '');
                  setEscalation('');
                }}
              >
                <option value="">Select a disposition…</option>
                {DISPOSITIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {disposition !== '' && (
                <p className="mt-1 text-xs text-muted">
                  {DISPOSITIONS.find(option => option.value === disposition)?.hint}
                </p>
              )}
            </div>

            {escalating && (
              <div>
                <label className="label" htmlFor="fraud-escalation">
                  Escalate to
                </label>
                <select
                  id="fraud-escalation"
                  className="input"
                  value={escalation}
                  onChange={event =>
                    setEscalation(event.target.value as FraudEscalationAction | '')
                  }
                >
                  <option value="">Select an action…</option>
                  {ESCALATIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-muted">
                  Opens a request. A second admin must approve it before anything happens.
                </p>
              </div>
            )}

            {suspending && (
              <div>
                <label className="label" htmlFor="fraud-duration">
                  Suspension length (days)
                </label>
                <input
                  id="fraud-duration"
                  className="input"
                  type="number"
                  inputMode="numeric"
                  min={SUSPENSION_MIN_DAYS}
                  max={SUSPENSION_MAX_DAYS}
                  value={durationDays}
                  onChange={event => setDurationDays(event.target.value)}
                />
                <p className="mt-1 text-xs text-muted">
                  {SUSPENSION_MIN_DAYS}–{SUSPENSION_MAX_DAYS} days. Required, because a suspension
                  with no length is indefinite.
                </p>
              </div>
            )}

            <div>
              <label className="label" htmlFor="fraud-rationale">
                Rationale (required)
              </label>
              <textarea
                id="fraud-rationale"
                className="input"
                rows={3}
                value={rationale}
                maxLength={RATIONALE_MAX_LENGTH}
                placeholder="What did you conclude, and why?"
                onChange={event => setRationale(event.target.value)}
              />
              <p className="mt-1 text-xs text-muted">
                {rationale.length}/{RATIONALE_MAX_LENGTH}. Recorded against the donation, not the
                public audit log.
              </p>
            </div>

            {!evidenceLoaded && (
              <p className="text-xs text-red-400">
                Resolution is unavailable until this donor&apos;s donations load — a case cannot be
                closed on evidence nobody has seen.
              </p>
            )}

            <button
              type="button"
              className="btn btn-primary w-full"
              disabled={!canSubmit}
              onClick={submit}
            >
              {resolve.isPending ? 'Saving…' : escalating ? 'Escalate' : 'Resolve case'}
            </button>
          </div>
        }
      >
        {donorCase.isPending && <p className="text-sm text-muted">Loading donations…</p>}

        {donorCase.isError && (
          <ErrorCard
            title="Could not load this donor's flagged donations"
            error={donorCase.error}
            onRetry={() => donorCase.refetch()}
          />
        )}

        {donorCase.isSuccess && (
          <FraudReviewCaseView
            detail={donorCase.data.data}
            onReceiptExpired={() => donorCase.refetch()}
          />
        )}
      </DetailDrawer>
    </div>
  );
}
